import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ConnectedProps, connect } from "react-redux";

import { usePerformanceBreadcrumbs } from "../../Common/Hooks/usePerformanceBreadcrumbs";
import FlowPerformanceBody from "./FlowPerformanceBody";

import cn from "classnames";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import { type IWizardStep, bindAction } from "interfaces";
import { type EntityType } from "../../../../components/charts/types/Sankey";
import ReportingExport from "../../../../components/reportingExport/ReportingExport";
import { RequestStatusRenderer } from "../../../../components/requestStatsRenderer/RequestStatusRenderer";
import { FeatureFlags } from "../../../../featureFlags";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import dateTimeUtils from "../../../../utils/dateTimeUtils";
import { RStatus } from "../../../Application/globaltypes/fetchRequest";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { reset, selectDateRangeValue, selectDateRanges } from "../state/slices/flowPerformanceSlice";
import { fetchFlowDateRanges } from "../state/thunks/flowPerformanceThunk";
import { FlowFilter } from "./Filter/FlowPerformanceFilter";

import { selectIsReportEnabled } from "features/Reporting/state/toolbar/toolbarSlice";
import "../../Common/utils/performanceSCSSUtils.scss";
import "./flowPerformance.scss";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { allCustomerAccounts, normalizeStatusFilter } from "features/Library/Common/utils/performanceUtils";
import { useLocation } from "react-router";
import { useReportingAccountType } from "components/reportingFilter/ReportingFilterRequests";
import { type AccountType } from "../../../../interfaces/Account";
import { flowInformationSelector, selectFlowHasBeenPublished, selectIsFlowEmailOnly } from "../state/selectors";

const SERVER_FORMAT = "MM/DD/YYYY HH:mm:ss";

function formatForServer(date: string) {
  return moment(date).format(SERVER_FORMAT);
}

export type Props = PropsFromRedux &
  IWizardStep & {
    flowId: number;
    flowTitle: string;
  };

export interface AssetInfo {
  id: number;
  type: EntityType;
  name: string;
}

export const FlowPerformance: React.FC<Props> = ({
  flowId,
  flowTitle,
  dateRangeState,
  dateRangeValue,
  getDateRanges,
  reset,
  accountName,
  acceptHandlers,
  isExportEnabled,
  reportEnabled,
  accountId,
  accountType,
  publisherId,
  isFlowHasBeenPublished,
  isFlowEmailOnly,
}) => {
  const { state } = useLocation();
  const type = useReportingAccountType(accountId, true, publisherId);

  // Determine if account filter should show (publisher dropdown & accounts)
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);

  const [selectedAsset, setSelectedAsset] = useState<AssetInfo>();
  const [filterIndex, setFilterIndex] = useState(-1);
  const [currentFilter, setCurrentFilter] = useState<{
    date: number;
    includeOwnData?: boolean;
    accounts?: any[];
    type: AccountType;
    isDistinct: boolean;
    versionNumber: number;
    priority: number[];
    status: number[];
  }>({
    date: filterIndex,
    isDistinct: false,
    type: type ?? "account",
    versionNumber: filterIndex,
    priority: [],
    status: [],
  });

  const initialFilterSet = useRef(false);
  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  const reportFlagEnabled = useFeatureFlag(FeatureFlags.FlowReport);

  useEffect(() => {
    if (accountFilterShouldShow)
      setCurrentFilter({
        date: -1,
        includeOwnData: true,
        accounts: [allCustomerAccounts.text],
        type: type ?? "account",
        isDistinct: false,
        versionNumber: -1,
        priority: [],
        status: [],
      });
  }, [accountFilterShouldShow, type, accountId]);

  useEffect(() => {
    if (filterIndex !== currentFilter.date) setFilterIndex(currentFilter.date);
  }, [filterIndex, currentFilter]);

  useEffect(() => {
    if (reportFlagEnabled) {
      getDateRanges(flowId);
    }

    return () => {
      reset();
    };
  }, [flowId, getDateRanges, reportFlagEnabled, reset]);

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLastPresentDate = useCallback(() => {
    if (dateRangeValue.length === 0) return -1;
    const lastPresentDate = dateRangeValue.findIndex((d) => d.DATETIME_END === null && d.OBSOLETE !== true);
    return lastPresentDate === -1 ? dateRangeValue.length - 1 : lastPresentDate;
  }, [dateRangeValue]);

  // Load filter index to last value on first load
  useEffect(() => {
    if (dateRangeState.status === RStatus.Got && !initialFilterSet.current) {
      initialFilterSet.current = true;

      const whichDateInRange = (dateTo: string) => {
        const date = new Date(dateTo);

        for (let i = 0; i < dateRangeValue.length; i++) {
          const range = dateRangeValue[i];
          const startDate = new Date(range.DATETIME_START);
          const endDate = range.DATETIME_END ? new Date(range.DATETIME_END) : null;

          if (
            date >= startDate &&
            (endDate === null || date <= endDate) &&
            range.VERSION !== null &&
            range.OBSOLETE !== true
          ) {
            return i;
          }
        }
        return dateRangeValue.length - 1;
      };

      const navigationDateIndex = state?.dateTo ? whichDateInRange(state.dateTo) : getLastPresentDate();

      setFilterIndex(navigationDateIndex);
      setCurrentFilter({
        ...currentFilter,
        date: navigationDateIndex,
        versionNumber: navigationDateIndex,
      });
    }
  }, [dateRangeState.status, dateRangeValue, currentFilter, state, getLastPresentDate]);

  const filterParamFormatted = useMemo(() => {
    let tempFilterIndex = filterIndex;
    if (dateRangeValue.length > 0 && filterIndex === -1) tempFilterIndex = dateRangeValue.length - 1;
    if (tempFilterIndex < 0) {
      return null;
    }
    let date = dateRangeValue[tempFilterIndex];
    if (!date) return null;

    const status = normalizeStatusFilter(currentFilter.status);

    return {
      dateFrom: formatForServer(date.DATETIME_START),
      dateTo:
        date.DATETIME_END === null
          ? dateTimeUtils.formatCurrentDateToUtc(SERVER_FORMAT)
          : formatForServer(date.DATETIME_END),
      ...(accountFilterShouldShow && { includeMyData: currentFilter.includeOwnData }),
      ...(accountFilterShouldShow && { accounts: currentFilter.accounts }),
      isDistinct: currentFilter.isDistinct,
      type: currentFilter.type,
      versionNumber: date.VERSION,
      priority: currentFilter.priority,
      status,
    };
  }, [
    dateRangeValue,
    filterIndex,
    accountFilterShouldShow,
    currentFilter.includeOwnData,
    currentFilter.accounts,
    currentFilter.type,
    currentFilter.isDistinct,
    currentFilter.priority,
    currentFilter.status,
  ]);

  const handleFilterReset = useCallback(() => {
    setFilterIndex(dateRangeValue.length - 1);
    setCurrentFilter({
      date: getLastPresentDate(),
      ...(accountFilterShouldShow && { includeOwnData: true }),
      ...(accountFilterShouldShow && { accounts: ["All Customer Accounts"] }),
      isDistinct: false,
      type: type ?? "account",
      versionNumber: currentFilter.versionNumber,
      priority: [],
      status: [],
    });
  }, [dateRangeValue.length, getLastPresentDate, accountFilterShouldShow, type, currentFilter.versionNumber]);

  if (!reportFlagEnabled) {
    return <ReportUnavailable />;
  }

  const tools = (
    <>
      <div className="flowActions">
        <FlowFilter
          accountName={accountName}
          currentFilter={currentFilter}
          dateRanges={dateRangeValue}
          setCurrentFilter={setCurrentFilter}
          includeAccountsDropdown={accountFilterShouldShow ?? false}
          accountId={accountId}
          handleReset={handleFilterReset}
          publisherId={publisherId}
          // Disabled filter if drilled down
          // as the lower item may not exist
          // in the new flow version
          disabled={hasBreadcrumbsToDisplay}
        />
        {isExportEnabled && <ReportingExport />}
      </div>
    </>
  );

  const toolbar = reportEnabled ? tools : <div />;

  return (
    <div className={cn("flowPerformanceRoot stretch", { drilldownReportUnavailable: !reportEnabled })}>
      {/* Skips renderer check if feature flag is disabled */}
      <RequestStatusRenderer state={dateRangeState}>
        <header className="flowPerformanceHeader">
          {hasBreadcrumbsToDisplay ? <div>{domElements}</div> : <h2 className="performanceTitle">Summary</h2>}
          {selectedAsset ? toolbar : tools}
        </header>

        <div className={cn("flowPerformanceRoot", { flowPerformanceBody: selectedAsset && !reportEnabled })}>
          <FlowPerformanceBody
            includeAccounts={accountFilterShouldShow && currentFilter.accounts && currentFilter.accounts.length > 0}
            setSelectedAssetFlow={setSelectedAsset}
            flowId={flowId}
            flowTitle={flowTitle}
            registerBreadcrumb={registerBreadcrumb}
            dateFilter={filterParamFormatted}
            appliedStatus={currentFilter.status}
            canNotify={isFlowHasBeenPublished && !isFlowEmailOnly}
          />
        </div>
      </RequestStatusRenderer>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    dateRangeState: selectDateRanges(state),
    dateRangeValue: selectDateRangeValue(state),
    isExportEnabled: selectIsExportActive(state),
    reportEnabled: selectIsReportEnabled(state),
    accountId: state.userProfile.accountId,
    accountName: state.accounts.accountInformation.accountInfo.accountName,
    accountType: state.userProfile.accountTypeId,
    publisherId: flowInformationSelector(state).info.ownerId,
    isFlowHasBeenPublished: selectFlowHasBeenPublished(state),
    isFlowEmailOnly: selectIsFlowEmailOnly(state),
  };
};

/* istanbul ignore next */
const mapActionToProps = (dispatch: AppDispatch) => {
  return {
    getDateRanges: bindAction(fetchFlowDateRanges, dispatch),
    reset: bindAction(reset, dispatch),
  };
};

const connector = connect(mapStateToProps, mapActionToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(FlowPerformance);
export default ConnectedComponent;
